import { cva } from "class-variance-authority";
import type { ReactNode } from "react";
import { useTranslation } from "../../i18n";
import { cn, getInitials } from "../../utils";
import { Skeleton } from "../Skeleton";
import { fontClassMap } from "./constants";
import type { AvatarProps } from "./types";

const avatarVariants = cva("inline-flex items-center box-border", {
  variants: {
    size: {
      xs: "gap-2",
      sm: "gap-2",
      md: "gap-3",
      lg: "gap-4",
      xl: "gap-4",
      "2xl": "gap-4",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

const avatarCircleVariants = cva(
  "flex items-center justify-center flex-shrink-0 bg-neutral-100 border border-neutral-500/10 select-none overflow-hidden",
  {
    variants: {
      size: {
        xs: "w-6 h-6",
        sm: "w-8 h-8",
        md: "w-10 h-10",
        lg: "w-12 h-12",
        xl: "w-14 h-14",
        "2xl": "w-16 h-16",
      },
      shape: {
        round: "rounded-full",
        rounded: "rounded-lg",
      },
    },
    compoundVariants: [
      {
        size: "xs",
        shape: "rounded",
        className: "rounded-md",
      },
    ],
    defaultVariants: {
      size: "md",
      shape: "rounded",
    },
  },
);

export const Avatar = ({
  size = "md",
  shape = "rounded",
  text,
  subtext,
  initials,
  src,
  alt,
  showDetails = false,
  className,
  loading,
  placeholderIcon,
  ...props
}: AvatarProps) => {
  const { t } = useTranslation();
  const letters: string = initials || getInitials(text);

  const imgJSX = loading ? (
    <Skeleton
      className={cn(
        fontClassMap[size].skeletonImageSize,
        shape === "round" ? "rounded-full" : "rounded-lg",
        size === "xs" ? "rounded-md" : "",
      )}
    />
  ) : (
    <div className={avatarCircleVariants({ size, shape })}>
      {src && (
        <img
          className="w-full h-full object-cover"
          alt={alt || t("avatar.imageAlt")}
          src={src}
        />
      )}
      {!src && placeholderIcon && (
        <span className="flex items-center justify-center text-neutral-500">
          {placeholderIcon}
        </span>
      )}
      {!src && !placeholderIcon && letters && (
        <span className={cn("text-neutral-500", fontClassMap[size].letters)}>
          {letters}
        </span>
      )}
    </div>
  );

  const titleJSX: ReactNode =
    text &&
    (loading ? (
      <Skeleton
        className={cn(
          fontClassMap[size].skeletonTitleWidth,
          fontClassMap[size].skeletonTitleHeight,
        )}
      />
    ) : (
      <div className={cn("text-neutral-500", fontClassMap[size].title)}>
        {text}
      </div>
    ));

  const subTextJSX: ReactNode =
    subtext &&
    (loading ? (
      <Skeleton
        className={cn(
          fontClassMap[size].skeletonSubtextWidth,
          fontClassMap[size].skeletonSubtextHeight,
        )}
      />
    ) : (
      <div className={cn("text-neutral-500/50", fontClassMap[size].subtext)}>
        {subtext}
      </div>
    ));

  return (
    <div {...props} className={cn(avatarVariants({ size }), className)}>
      {imgJSX}
      {showDetails && (text || subtext) && (
        <div className={cn("flex flex-col", loading && "gap-0.5")}>
          {titleJSX}
          {subTextJSX}
        </div>
      )}
    </div>
  );
};
